import React, { useEffect, useRef, useState } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { motion, AnimatePresence } from 'framer-motion';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { useForm } from 'react-hook-form';
import toast from 'react-hot-toast';
import {
  ShoppingCartIcon,
  PlusIcon,
  XMarkIcon,
  ArrowRightIcon,
  TrashIcon,
} from '@heroicons/react/24/outline';
import { useFamilyStore } from '../../store/familyStore';
import api from '../../api/client';
import { useMarkCategoryRead } from '../../hooks/useMarkCategoryRead';

interface ShoppingList {
  id: string;
  name: string;
  color: string;
  icon: string;
  status: 'active' | 'completed' | 'archived';
  store_name: string | null;
  total_items: number;
  checked_items: number;
  created_at: string;
}

interface CreateListForm {
  name: string;
  store_name: string;
  color: string;
}

export default function ShoppingLists() {
  const navigate = useNavigate();
  const queryClient = useQueryClient();
  const { currentFamily } = useFamilyStore();
  const [showCreateModal, setShowCreateModal] = useState(false);
  const [highlightId, setHighlightId] = useState<string | null>(null);
  
  // Mark shopping notifications as read when visiting this page
  useMarkCategoryRead('shopping');
  const wsRef = useRef<WebSocket | null>(null);

  const { register, handleSubmit, reset, formState: { errors } } = useForm<CreateListForm>({
    defaultValues: { color: '#10b981' }
  });

  const { data: lists, isLoading } = useQuery({
    queryKey: ['shoppingLists', currentFamily?.id],
    queryFn: async () => {
      if (!currentFamily?.id) return [];
      const response = await api.get(`/shopping/family/${currentFamily.id}`);
      return response.data as ShoppingList[];
    },
    enabled: !!currentFamily?.id,
  });

  const createMutation = useMutation({
    mutationFn: async (data: CreateListForm) => {
      if (!currentFamily?.id) throw new Error('No family selected');
      const response = await api.post(`/shopping/family/${currentFamily.id}`, data);
      return response.data;
    },
    onSuccess: (data) => {
      queryClient.invalidateQueries({ queryKey: ['shoppingLists'] });
      toast.success('List created!');
      setShowCreateModal(false);
      reset();
      navigate(`/shopping/${data.id}`);
    },
    onError: (error: any) => {
      toast.error(error.response?.data?.detail || 'Failed to create list');
    },
  });

  const deleteMutation = useMutation({
    mutationFn: async (listId: string) => {
      await api.delete(`/shopping/${listId}`);
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['shoppingLists'] });
      toast.success('List deleted');
    },
    onError: () => toast.error('Failed to delete list'),
  });

  const colors = [
    '#10b981', '#14b8a6', '#0ea5e9', '#6366f1', '#8b5cf6',
    '#d946ef', '#ec4899', '#f43f5e', '#f97316', '#eab308',
  ];

  // Live websocket for realtime updates
  useEffect(() => {
    const token = localStorage.getItem('access_token');
    if (!currentFamily?.id || !token) return;

    const base = (import.meta.env.VITE_API_URL || '').replace(/^http/, 'ws');
    if (!base) return;

    const socket = new WebSocket(`${base}/ws/families/${currentFamily.id}?token=${token}`);
    wsRef.current = socket;

    socket.onmessage = (event) => {
      try {
        const payload = JSON.parse(event.data);
        if (payload.type === 'shopping_list_created') {
          const list: ShoppingList = payload.data;
          queryClient.setQueryData<ShoppingList[] | undefined>(
            ['shoppingLists', currentFamily.id],
            (old) => {
              if (!old) return [list];
              const exists = old.find((l) => l.id === list.id);
              if (exists) return old;
              return [list, ...old];
            }
          );
          setHighlightId(list.id);
          setTimeout(() => setHighlightId(null), 1200);
        }
        if (payload.type === 'shopping_item_added') {
          const item = payload.data;
          // Optional: could surface toast; we keep silent to avoid noise
        }
        if (payload.type === 'shopping_list_deleted') {
          const id = payload.data.id;
          queryClient.setQueryData<ShoppingList[] | undefined>(
            ['shoppingLists', currentFamily.id],
            (old) => old?.filter((l) => l.id !== id)
          );
        }
      } catch {
        // ignore malformed events
      }
    };

    socket.onclose = () => {
      wsRef.current = null;
    };

    return () => {
      socket.close();
      wsRef.current = null;
    };
  }, [currentFamily?.id, queryClient]);

  if (!currentFamily) {
    return (
      <div className="page-content">
        <div className="card text-center py-16">
          <ShoppingCartIcon className="w-16 h-16 mx-auto mb-4 text-surface-500" />
          <h2 className="text-xl font-bold text-white mb-2">No Family Selected</h2>
          <p className="text-surface-400 mb-6">Select or create a family to manage shopping lists</p>
          <Link to="/families" className="btn-primary inline-flex">Go to Families</Link>
        </div>
      </div>
    );
  }

  return (
    <div className="page-content">
      {/* Header */}
      <div className="flex items-center justify-between mb-6">
        <div>
          <h1 className="text-2xl font-bold text-white">Shopping Lists</h1>
          <p className="text-surface-400">{currentFamily.name}</p>
        </div>
        <button onClick={() => setShowCreateModal(true)} className="flex items-center gap-1.5 px-2.5 py-1 rounded bg-primary-500 hover:bg-primary-600 text-xs text-white transition-colors">
          <PlusIcon className="w-3.5 h-3.5" />
          <span className="hidden sm:inline">New List</span>
        </button>
      </div>

      {/* Lists */}
      {isLoading ? (
        <div className="flex items-center justify-center py-20">
          <div className="w-10 h-10 border-2 border-primary-500/30 border-t-primary-500 rounded-full animate-spin" />
        </div>
      ) : lists?.length === 0 ? (
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          className="card text-center py-16"
        >
          <ShoppingCartIcon className="w-16 h-16 mx-auto mb-4 text-surface-500" />
          <h2 className="text-xl font-bold text-white mb-2">No Shopping Lists</h2>
          <p className="text-surface-400 mb-6">Create your first shopping list</p>
          <button onClick={() => setShowCreateModal(true)} className="inline-flex items-center gap-1.5 px-2.5 py-1 rounded bg-primary-500 hover:bg-primary-600 text-xs text-white transition-colors">
            <PlusIcon className="w-3.5 h-3.5" />
            <span>Create List</span>
          </button>
        </motion.div>
      ) : (
        <div className="grid sm:grid-cols-2 gap-4">
          {lists?.map((list, index) => (
            <motion.div
              key={list.id}
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ delay: index * 0.05 }}
            >
              <Link
                to={`/shopping/${list.id}`}
                className={`card-interactive block relative ${
                  highlightId === list.id ? 'ring-2 ring-primary-400 shadow-primary-400/30 shadow-lg' : ''
                }`}
              >
                {highlightId === list.id && (
                  <span className="absolute -top-2 -right-2 bg-primary-500 text-white text-xs font-semibold px-2 py-1 rounded-full shadow-lg">
                    NEW
                  </span>
                )}
                <div className="flex items-start justify-between mb-4">
                  <div
                    className="w-12 h-12 rounded-xl flex items-center justify-center"
                    style={{ backgroundColor: `${list.color}20` }}
                  >
                    <ShoppingCartIcon className="w-6 h-6" style={{ color: list.color }} />
                  </div>
                  <div className="flex items-center gap-2">
                    {list.status === 'completed' && (
                      <span className="badge-success">Completed</span>
                    )}
                    <button
                      onClick={(e) => {
                        e.preventDefault();
                        if (window.confirm('Delete this list?')) {
                          deleteMutation.mutate(list.id);
                        }
                      }}
                      className="btn-icon text-surface-500 hover:text-red-400"
                    >
                      <TrashIcon className="w-5 h-5" />
                    </button>
                  </div>
                </div>
                <h3 className="text-lg font-semibold text-white mb-1">{list.name}</h3>
                {list.store_name && (
                  <p className="text-sm text-surface-400 mb-3">{list.store_name}</p>
                )}
                <div className="flex items-center justify-between">
                  <span className="text-sm text-surface-400">
                    {list.checked_items}/{list.total_items} items
                  </span>
                  <ArrowRightIcon className="w-5 h-5 text-surface-500" />
                </div>
                {list.total_items > 0 && (
                  <div className="mt-3 h-1.5 bg-surface-700 rounded-full overflow-hidden">
                    <div
                      className="h-full rounded-full transition-all"
                      style={{
                        width: `${(list.checked_items / list.total_items) * 100}%`,
                        backgroundColor: list.color,
                      }}
                    />
                  </div>
                )}
              </Link>
            </motion.div>
          ))}
        </div>
      )}

      {/* Create Modal */}
      <AnimatePresence>
        {showCreateModal && (
          <>
            <motion.div
              initial={{ opacity: 0 }}
              animate={{ opacity: 1 }}
              exit={{ opacity: 0 }}
              className="modal-overlay"
              onClick={() => setShowCreateModal(false)}
            />
            <motion.div
              initial={{ opacity: 0, scale: 0.95, y: 50 }}
              animate={{ opacity: 1, scale: 1, y: 0 }}
              exit={{ opacity: 0, scale: 0.95, y: 50 }}
              className="modal-content"
            >
              <div className="flex items-center justify-between mb-6">
                <h2 className="text-xl font-bold text-white">New Shopping List</h2>
                <button onClick={() => setShowCreateModal(false)} className="btn-icon">
                  <XMarkIcon className="w-6 h-6" />
                </button>
              </div>

              <form onSubmit={handleSubmit((data) => createMutation.mutate(data))} className="space-y-4">
                <div className="input-group">
                  <label className="input-label">List Name</label>
                  <input
                    type="text"
                    className="input"
                    placeholder="Weekly Groceries"
                    {...register('name', { required: 'Name is required' })}
                  />
                  {errors.name && <p className="text-sm text-red-400">{errors.name.message}</p>}
                </div>

                <div className="input-group">
                  <label className="input-label">Store (optional)</label>
                  <input
                    type="text"
                    className="input"
                    placeholder="Walmart, Target, etc."
                    {...register('store_name')}
                  />
                </div>

                <div className="input-group">
                  <label className="input-label">Color</label>
                  <div className="flex flex-wrap gap-2">
                    {colors.map((color) => (
                      <label key={color} className="cursor-pointer">
                        <input
                          type="radio"
                          value={color}
                          className="sr-only peer"
                          {...register('color')}
                        />
                        <div
                          className="w-8 h-8 rounded-lg border-2 border-transparent peer-checked:border-white transition-all"
                          style={{ backgroundColor: color }}
                        />
                      </label>
                    ))}
                  </div>
                </div>

                <div className="flex gap-3 pt-4">
                  <button type="button" onClick={() => setShowCreateModal(false)} className="btn-secondary flex-1">
                    Cancel
                  </button>
                  <button type="submit" disabled={createMutation.isPending} className="btn-primary flex-1">
                    {createMutation.isPending ? (
                      <div className="w-5 h-5 border-2 border-white/30 border-t-white rounded-full animate-spin" />
                    ) : (
                      'Create'
                    )}
                  </button>
                </div>
              </form>
            </motion.div>
          </>
        )}
      </AnimatePresence>
    </div>
  );
}

